import * as THREE from 'three';
import type { Dog } from '../dog/Dog';
import type { CareEvent } from '../care/CareRecord';

/**
 * Doing her business. A wee at the end of a sniff-walk now and then (squat,
 * kick the grass, a dark patch that fades); a poo some time after a meal
 * (circle, squat, a small pile). Piles are scooped with a tap; left alone they
 * show up in her status. Matter-of-fact, no jokes.
 */
export interface Pile {
  x: number;
  z: number;
  at: number;
}
export interface BusinessState {
  lastPee: number;
  lastPoop: number;
  lastMealAt: number;
  piles: Pile[];
}

const PEE_GAP = 20 * 60_000; // ms between wees
const POOP_AFTER_MEAL = [20 * 60_000, 6 * 3600_000];
const PATCH_FADE = 60; // seconds
const OLD_PILE = 4 * 3600_000;

const _v = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

export class Business {
  state: BusinessState;
  onMessage?: (text: string) => void;
  onCare?: (ev: CareEvent) => void;
  onChange?: () => void;
  scooping: { pile: THREE.Object3D; t: number } | null = null;
  /** true while it's raining: patches fade faster */
  raining = false;

  private pileGroup = new THREE.Group();
  private patches: { mesh: THREE.Mesh; t: number }[] = [];
  private busy = false;
  private saveKey: string;
  private pileGeo = new THREE.SphereGeometry(0.035, 8, 6);
  private pileMat = new THREE.MeshStandardMaterial({ color: 0x4a3218, roughness: 1 });
  private patchMat = new THREE.MeshStandardMaterial({ color: 0x3e5a2a, transparent: true, opacity: 0.4, depthWrite: false });

  constructor(
    private scene: THREE.Object3D,
    private dog: Dog,
    private forbidden: THREE.Vector3[],
  ) {
    this.saveKey = `lunaloo.business.${dog.profile.id}`;
    this.state = this.load();
    scene.add(this.pileGroup);
    for (const p of this.state.piles) this.addPileMesh(p);
    dog.onSniffEnd = () => this.maybePee();
    dog.onMealEnd = () => {
      this.state.lastMealAt = Date.now();
      this.save();
    };
  }

  get pickables(): THREE.Object3D[] {
    return [this.pileGroup];
  }

  // ---------- state
  private load(): BusinessState {
    const fresh: BusinessState = { lastPee: 0, lastPoop: 0, lastMealAt: 0, piles: [] };
    try {
      const raw = localStorage.getItem(this.saveKey);
      return raw ? { ...fresh, ...(JSON.parse(raw) as Partial<BusinessState>) } : fresh;
    } catch {
      return fresh;
    }
  }
  save(): void {
    try {
      localStorage.setItem(this.saveKey, JSON.stringify(this.state));
    } catch {
      /* no storage */
    }
    this.onChange?.();
  }
  snapshot(): BusinessState & { at: number } {
    return { ...this.state, at: Date.now() };
  }
  applyRemote(s: BusinessState & { at: number }): void {
    // union of piles (scooped elsewhere disappears when the other phone pushes its list)
    this.state = { lastPee: Math.max(this.state.lastPee, s.lastPee), lastPoop: Math.max(this.state.lastPoop, s.lastPoop), lastMealAt: Math.max(this.state.lastMealAt, s.lastMealAt), piles: s.piles ?? [] };
    this.pileGroup.clear();
    for (const p of this.state.piles) this.addPileMesh(p);
    this.save();
  }

  statusWords(): string[] {
    const old = this.state.piles.some((p) => Date.now() - p.at > OLD_PILE);
    return old ? ['yard needs scooping'] : this.state.piles.length >= 3 ? ['yard needs scooping'] : [];
  }

  // ---------- meshes
  private addPileMesh(p: Pile): THREE.Object3D {
    const g = new THREE.Group();
    for (const [dx, dy, dz, s] of [
      [0, 0.03, 0, 1.1],
      [0.035, 0.028, 0.02, 0.85],
      [-0.03, 0.026, 0.025, 0.8],
      [0.005, 0.07, 0.005, 0.7],
    ] as const) {
      const m = new THREE.Mesh(this.pileGeo, this.pileMat);
      m.position.set(dx, dy, dz);
      m.scale.setScalar(s);
      m.castShadow = true;
      g.add(m);
    }
    g.position.set(p.x, 0, p.z);
    g.userData.tapName = 'pile';
    g.userData.pile = p;
    this.pileGroup.add(g);
    return g;
  }

  private addPatch(x: number, z: number): void {
    const m = new THREE.Mesh(new THREE.CircleGeometry(0.16, 16), this.patchMat.clone());
    m.rotation.x = -Math.PI / 2;
    m.position.set(x, 0.004, z);
    this.scene.add(m);
    this.patches.push({ mesh: m, t: 0 });
  }

  private okSpot(x: number, z: number): boolean {
    for (const f of this.forbidden) if (f.distanceToSquared(_v.set(x, 0, z)) < 1.2 * 1.2) return false;
    return true;
  }

  // ---------- triggers
  private maybePee(): void {
    if (this.busy || this.dog.busy) return;
    if (Date.now() - this.state.lastPee < PEE_GAP) return;
    if (Math.random() > 0.45) return;
    const p = this.dog.root.position;
    if (!this.okSpot(p.x, p.z)) return;
    this.busy = true;
    this.state.lastPee = Date.now();
    this.dog.doBusiness('pee', () => this.addPatch(p.x, p.z), () => {
      this.busy = false;
      this.save();
    });
  }

  private maybePoop(): void {
    if (this.busy || !this.dog.idle) return;
    const since = Date.now() - this.state.lastMealAt;
    if (!this.state.lastMealAt || since < POOP_AFTER_MEAL[0] || since > POOP_AFTER_MEAL[1]) return;
    if (this.state.lastPoop > this.state.lastMealAt) return; // already went after this meal
    if (Math.random() > 0.02) return; // checked ~once a second while idle: a few minutes' spread
    const p = this.dog.root.position;
    if (!this.okSpot(p.x, p.z)) return;
    this.busy = true;
    this.state.lastPoop = Date.now();
    this.dog.doBusiness(
      'poop',
      () => {
        // the pile lands just behind her
        _v.set(0, 0, -0.35 * this.dog.profile.size).applyAxisAngle(_up, this.dog.root.rotation.y).add(this.dog.root.position);
        const pile: Pile = { x: _v.x, z: _v.z, at: Date.now() };
        this.state.piles.push(pile);
        this.addPileMesh(pile);
        this.save();
      },
      () => {
        this.busy = false;
        this.save();
      },
    );
  }

  /** tap on a pile: a two-second scoop */
  tapPile(obj: THREE.Object3D): void {
    if (this.scooping) return;
    let o: THREE.Object3D | null = obj;
    while (o && !o.userData.pile) o = o.parent;
    if (!o) return;
    this.scooping = { pile: o, t: 0 };
  }

  private finishScoop(): void {
    const s = this.scooping!;
    this.scooping = null;
    const pile = s.pile.userData.pile as Pile;
    this.state.piles = this.state.piles.filter((p) => p !== pile && !(p.x === pile.x && p.z === pile.z));
    this.pileGroup.remove(s.pile);
    this.onMessage?.('scooped');
    this.onCare?.('scoop');
    this.save();
  }

  // ---------- per frame
  private poopCheck = 0;
  update(dt: number): void {
    this.poopCheck += dt;
    if (this.poopCheck > 1) {
      this.poopCheck = 0;
      this.maybePoop();
    }
    if (this.scooping) {
      this.scooping.t += dt;
      const n = Math.round((this.scooping.t / 2) * 6);
      this.onMessage?.(`scooping… ${'▮'.repeat(n)}${'▯'.repeat(6 - n)}`);
      if (this.scooping.t >= 2) this.finishScoop();
    }
    const fade = PATCH_FADE * (this.raining ? 0.35 : 1);
    for (let i = this.patches.length - 1; i >= 0; i--) {
      const p = this.patches[i];
      p.t += dt;
      (p.mesh.material as THREE.MeshStandardMaterial).opacity = 0.4 * Math.max(0, 1 - p.t / fade);
      if (p.t > fade) {
        this.scene.remove(p.mesh);
        this.patches.splice(i, 1);
      }
    }
  }
}
